import bbox from '@turf/bbox';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Button, Popover } from 'react-bootstrap';
import Map, { Layer, Source } from 'react-map-gl';
import { useBoundaryLayers } from '../hooks/useBoundaryLayers';
import PreviewButton, { monoStyle } from './PreviewButton';

const statusLabel = (status) => {
  switch (status) {
    case 'pre_trap_catch_threshold_met':
      return 'Threshold Met';
    case 'accumulation_threshold_met':
      return 'Threshold Met';
    case 'average_threshold_met':
      return 'Threshold Met';
    case 'near_accumulation_threshold':
      return 'Near Threshold';
    case 'good':
      return 'No Action Required';
    case 'sprayed':
      return 'Sprayed';
    case 'no_data':
      return 'Not Monitored';
    default:
      return 'Unknown';
  }
};

// TODO: Refactor to use useMapState hook.
const SummaryMap = (props) => {
  const [viewport, setViewport] = useState({
    latitude: -40.9753799,
    longitude: 176.8903929,
    zoom: 4,
    height: 400,
    width: '100%'
  });
  const [mapStyle, setMapStyle] = useState(monoStyle);
  const [initialBoundsSet, setInitialBoundsSet] = useState(false);
  const [hoverInfo, setHoverInfo] = useState();
  const layers = useBoundaryLayers([0.2, 0.4, 4, 0.5]);
  const mapRef = useRef();

  const data = useMemo(() => {
    const geojson = {
      type: 'FeatureCollection',
      features: []
    };

    props.propertiesWithTrappingZones.forEach((property) => {
      const propertyStatus = props.propertyStatuses.find(
        (p) => p.propertyId === property.id
      );
      property.trapping_zones.forEach((trappingZone) => {
        const trappingZoneStatus = propertyStatus?.trappingZoneStatuses.find(
          (t) => t.trappingZoneId === trappingZone.id
        );
        const boundry = JSON.parse(trappingZone.boundry);

        if (boundry)
          geojson.features.push({
            type: 'Feature',
            geometry: boundry,
            properties: {
              status: trappingZoneStatus
                ? trappingZoneStatus.status
                : 'no_data',
              id: trappingZone.id,
              propertyId: property.id,
              propertyName: property.name,
              propertyIdentifier: property.identifier,
              trappingZoneId: trappingZone.id,
              trappingZoneName: trappingZone.name
            }
          });
      });
    });

    return geojson;
  }, [props.propertiesWithTrappingZones]);

  const fitBounds = (map) => {
    const features = data.features;
    if (features) {
      let minLng;
      let minLat;
      let maxLng;
      let maxLat;

      data.features.forEach((feature) => {
        const [newMinLng, newMinLat, newMaxLng, newMaxLat] = bbox(feature);
        if (minLng === undefined || newMinLng < minLng) minLng = newMinLng;
        if (maxLng === undefined || newMaxLng > maxLng) maxLng = newMaxLng;
        if (minLat === undefined || newMinLat < minLat) minLat = newMinLat;
        if (maxLat === undefined || newMaxLat > maxLat) maxLat = newMaxLat;
      });

      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat]
        ],
        { padding: 50, duration: 500 }
      );
    }
  };

  // Update the bounds of the map once the 100% width has been converted to an integer
  useEffect(() => {
    if (
      !initialBoundsSet &&
      data.features.length > 0 &&
      Number.isInteger(viewport.width)
    ) {
      fitBounds();
      setInitialBoundsSet(true);
    }
  }, [viewport, data]);

  const mapRefWrapper = useCallback((node) => {
    if (node) {
      if (data && !initialBoundsSet) {
        fitBounds(node);
        setInitialBoundsSet(true);
      }
    }
    mapRef.current = node;
  }, []);

  return data.features.length > 0 ? (
    <Map
      ref={mapRefWrapper}
      initialViewState={viewport}
      onViewportChange={(nextViewport) => setViewport(nextViewport)}
      mapStyle={`mapbox://styles/hortplus/${mapStyle}`}
      attributionControl={false}
      interactiveLayerIds={[
        'layerStyleThresholdMet',
        'layerStyleNearThreshold',
        'layerStyleGood',
        'layerStyleNoData'
      ]}
      onMouseMove={(e) => {
        const hoveredFeature = e.features && e.features[0];
        setHoverInfo(
          hoveredFeature
            ? {
                feature: hoveredFeature,
                x: e.originalEvent.offsetX,
                y: e.originalEvent.offsetY
              }
            : null
        );
      }}
      onClick={(e) => {
        const hoveredFeature = e.features && e.features[0];
        if (hoveredFeature && props.onClick)
          props.onClick(
            hoveredFeature.properties.propertyId,
            hoveredFeature.properties.trappingZoneId
          );
      }}
      mapboxAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
    >
      <div
        className='m-3'
        style={{
          position: 'absolute',
          top: 0,
          right: 0
        }}
      >
        <Button
          variant='light'
          onClick={() => fitBounds(mapRef.current)}
          className='m-3 float-right'
        >
          Reset
        </Button>
      </div>
      <Source id='trappingZones' type='geojson' data={data}>
        {layers.map((layer) => (
          <Layer key={layer.id} {...layer} />
        ))}
      </Source>
      {hoverInfo ? (
        <Popover
          id={hoverInfo.feature.properties.id}
          placement='auto'
          style={{
            left:
              viewport.width - hoverInfo.x < 200
                ? hoverInfo.x - 200
                : hoverInfo.x + 5,
            top:
              viewport.height - hoverInfo.y < 100
                ? hoverInfo.y - 90
                : hoverInfo.y,
            position: 'absolute',
            width: '200px',
            zIndex: 9,
            pointerEvents: 'none'
          }}
        >
          <Popover.Title>
            <b>{hoverInfo.feature.properties.propertyIdentifier}</b>{' '}
            {hoverInfo.feature.properties.propertyName}
          </Popover.Title>
          <Popover.Content>
            Production Site:{' '}
            <b>{hoverInfo.feature.properties.trappingZoneName}</b>
            <br />
            Status: <b>{statusLabel(hoverInfo.feature.properties.status)}</b>
          </Popover.Content>
        </Popover>
      ) : null}

      <PreviewButton
        setMapStyle={setMapStyle}
        mapStyle={mapStyle}
        viewport={viewport}
      />
    </Map>
  ) : null;
};

SummaryMap.propTypes = {
  // Authentication
  selectedDatabase: PropTypes.string,
  bearerToken: PropTypes.string.isRequired,
  refreshBearerToken: PropTypes.func.isRequired,

  // Data
  propertiesWithTrappingZones: PropTypes.array.isRequired,
  propertyStatuses: PropTypes.array.isRequired,

  // Misc
  onClick: PropTypes.func
};

export default observer(SummaryMap);
